import {
  scenarioToDemoMode,
  type EvaluationDashboardScenario,
} from "@/lib/evaluation-dashboard-scenario"
import {
  DEMO_CASE_5_TERM,
  DEMO_CASE_6_TERM,
  DEMO_CASE_7_TERM,
  DEMO_CASE_MIDWAY_TERM,
  DEMO_STATE_FIRST_CLOSED_TERM,
  DEMO_STATE_FIRST_LIVE_TERM,
  DEMO_STATE_READY_TERM,
  DEMO_STATE_SETUP_TERM,
  EVALUATION_REFERENCE_DATE,
  MOCK_EVALUATION_TERMS,
  resolveEvaluationTerm,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"

export type DashboardDemoMode =
  | "empty"
  | "stateSetup"
  | "stateReady"
  | "stateFirstLive"
  | "stateFirstClosed"
  | "stateGrowing"
  | "stateOptimized"
  | "case5"
  | "case6"
  | "case7"
  | "caseMidway"
  | null

export interface DashboardTermSlice {
  showNoTerms: boolean
  currentTerm?: EvaluationTerm
  lastTerm?: EvaluationTerm
  pastTerms: EvaluationTerm[]
  upcomingTerms: EvaluationTerm[]
  futureTerms: EvaluationTerm[]
  allTerms: EvaluationTerm[]
}

function allResolvedTerms(today: Date = EVALUATION_REFERENCE_DATE): EvaluationTerm[] {
  return MOCK_EVALUATION_TERMS.map(term => resolveEvaluationTerm(term, today))
}

export function parseDashboardDemo(search: string): DashboardDemoMode {
  if (typeof search !== "string") return null
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
  const demo = params.get("demo")
  const modes: DashboardDemoMode[] = [
    "empty",
    "stateSetup",
    "stateReady",
    "stateFirstLive",
    "stateFirstClosed",
    "stateGrowing",
    "stateOptimized",
    "case5",
    "case6",
    "case7",
    "caseMidway",
  ]
  if (modes.includes(demo as DashboardDemoMode)) return demo as DashboardDemoMode
  return null
}

export function resolveDashboardDemoMode(
  search: string,
  scenario?: EvaluationDashboardScenario | null,
): DashboardDemoMode {
  const urlDemo = parseDashboardDemo(search)
  const scenarioDemo = scenario ? scenarioToDemoMode(scenario) : null
  if (urlDemo === "empty" && scenario && scenario !== "onboarding") {
    return scenarioDemo
  }
  return urlDemo ?? scenarioDemo
}

export function getDashboardTermSlice(
  search: string,
  today: Date = EVALUATION_REFERENCE_DATE,
  scenario?: EvaluationDashboardScenario | null,
): DashboardTermSlice {
  const demo = resolveDashboardDemoMode(search, scenario)
  if (demo === "empty") {
    return {
      showNoTerms: true,
      pastTerms: [],
      upcomingTerms: [],
      futureTerms: [],
      allTerms: [],
    }
  }

  const terms = allResolvedTerms(today)
  const pastTerms = terms
    .filter(term => term.timelinePosition === "past")
    .sort((a, b) => a.startDate.localeCompare(b.startDate))

  let currentTerm = terms.find(term => term.timelinePosition === "current")
  let lastTerm = terms.find(term => term.timelinePosition === "last")

  if (demo === "case5") currentTerm = DEMO_CASE_5_TERM
  if (demo === "case6") currentTerm = DEMO_CASE_6_TERM
  if (demo === "case7") currentTerm = DEMO_CASE_7_TERM
  if (demo === "caseMidway") currentTerm = DEMO_CASE_MIDWAY_TERM
  if (demo === "stateSetup") currentTerm = DEMO_STATE_SETUP_TERM
  if (demo === "stateReady") currentTerm = DEMO_STATE_READY_TERM
  if (demo === "stateFirstLive") {
    currentTerm = DEMO_STATE_FIRST_LIVE_TERM
    lastTerm = undefined
  }
  if (demo === "stateFirstClosed") {
    currentTerm = undefined
    lastTerm = DEMO_STATE_FIRST_CLOSED_TERM
  }

  const allTerms = [...terms].sort((a, b) => {
    const timelineDelta = TIMELINE_SORT[a.timelinePosition] - TIMELINE_SORT[b.timelinePosition]
    if (timelineDelta !== 0) return timelineDelta
    return b.startDate.localeCompare(a.startDate)
  })

  return {
    showNoTerms: false,
    currentTerm,
    lastTerm,
    pastTerms,
    upcomingTerms: terms.filter(term => term.timelinePosition === "upcoming"),
    futureTerms: terms.filter(term => term.timelinePosition === "future"),
    allTerms,
  }
}

const TIMELINE_SORT: Record<EvaluationTerm["timelinePosition"], number> = {
  current: 0,
  last: 1,
  upcoming: 2,
  future: 3,
  past: 4,
}
